import React, { useState, useEffect, useCallback } from 'react';
import {
  getLeads, getIcpProfiles, getIcpProfile, createIcpProfile, updateIcpProfile, deleteIcpProfile,
  scoreLead, bulkScoreLeads, aiEnrichLead, submitEnrichmentReview,
  getDueFollowUps, completeFollowUp, getDefaultProfilingTemplate, getActiveSignals, getAiProviders,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import {
  Box, Typography, Alert, Tabs, Tab, Paper, Stack, Button, Chip, TextField, MenuItem,
  CircularProgress, FormControlLabel, Checkbox, DialogActions, Grid, LinearProgress,
} from '@mui/material';

export default function ProfilePage() {
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');

  const tabLabels = ['Dashboard', 'Scoring', 'ICP Profiles', 'Enrichment'];
  const tabKeys = ['dashboard', 'scoring', 'icp-profiles', 'enrichment'];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Profile &amp; Qualification</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        {tabLabels.map((label, i) => (
          <Tab key={tabKeys[i]} label={label} />
        ))}
      </Tabs>

      {activeTab === 0 && <DashboardTab setError={setError} />}
      {activeTab === 1 && <ScoringTab setError={setError} />}
      {activeTab === 2 && <IcpProfilesTab setError={setError} />}
      {activeTab === 3 && <EnrichmentTab setError={setError} />}
    </Box>
  );
}

// ─── DASHBOARD TAB ───
function DashboardTab({ setError }) {
  const [leads, setLeads] = useState([]);
  const [followUps, setFollowUps] = useState({ leadFollowUps: [], customerFollowUps: [] });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    Promise.all([
      getLeads().then(r => setLeads(r.data)),
      getDueFollowUps().then(r => setFollowUps(r.data)).catch(() => {}),
    ]).catch(() => setError('Failed to load dashboard data')).finally(() => setLoading(false));
  }, [setError]);

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  const fitBuckets = { High: 0, Medium: 0, Low: 0, 'No Score': 0 };
  let totalEnrichment = 0; let enrichedCount = 0;
  leads.forEach(p => {
    const pct = p.icpMatchPercentage;
    if (pct == null) fitBuckets['No Score']++;
    else if (pct >= 76) fitBuckets.High++;
    else if (pct >= 51) fitBuckets.Medium++;
    else fitBuckets.Low++;
    if (p.enrichmentCompleteness != null) { totalEnrichment += p.enrichmentCompleteness; enrichedCount++; }
  });
  const FIT_BUCKETS = ['High', 'Medium', 'Low', 'No Score'];
  const fitColorMap = { High: '#16a34a', Medium: '#dd6b20', Low: '#3182ce', 'No Score': '#718096' };
  const avgEnrichment = enrichedCount > 0 ? Math.round(totalEnrichment / enrichedCount) : 0;
  const allFollowUps = [...(followUps.leadFollowUps || []), ...(followUps.customerFollowUps || [])];

  const handleCompleteFollowUp = async (id, type) => {
    try {
      await completeFollowUp(id, type);
      const res = await getDueFollowUps();
      setFollowUps(res.data);
    } catch { setError('Failed to complete follow-up'); }
  };

  const followUpColumns = [
    { key: 'subject', header: 'Subject', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    { key: 'type', header: 'Type', render: (val) => <Chip label={val} size="small" /> },
    { key: 'followUpDate', header: 'Due Date', render: (val) => <Typography sx={{ color: '#e53e3e', fontSize: '0.875rem' }}>{new Date(val).toLocaleDateString()}</Typography> },
    { key: 'followUpAction', header: 'Follow-Up', render: (val) => val || '-' },
    {
      key: 'id', header: 'Actions', render: (_val, row) => {
        const isLead = row.leadId != null;
        return (
          <Button variant="contained" size="small" onClick={() => handleCompleteFollowUp(row.id, isLead ? 'lead' : 'customer')}>
            Complete
          </Button>
        );
      }
    },
  ];

  return (
    <Box>
      {/* Pipeline Funnel */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Pipeline by ICP Fit</Typography>
        <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap' }}>
          {FIT_BUCKETS.map(bucket => (
            <Box key={bucket} sx={{ flex: 1, textAlign: 'center' }}>
              <Box sx={{
                height: Math.max(40, (fitBuckets[bucket] / Math.max(leads.length, 1)) * 200),
                background: fitColorMap[bucket] + '30',
                border: `2px solid ${fitColorMap[bucket]}`,
                borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.5rem', fontWeight: 700, color: fitColorMap[bucket],
                minHeight: 40, transition: 'height 0.3s',
              }}>
                {fitBuckets[bucket]}
              </Box>
              <Typography sx={{ mt: 0.5, fontWeight: 600, color: fitColorMap[bucket] }}>{bucket}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Enrichment Stats */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Enrichment</Typography>
            <Typography sx={{ fontSize: '2rem', fontWeight: 700 }}>{avgEnrichment}%</Typography>
            <Typography sx={{ color: '#718096' }}>Average completeness</Typography>
            <Typography sx={{ mt: 1, fontSize: '0.9rem' }}>
              {leads.filter(p => (p.enrichmentCompleteness || 0) >= 80).length} fully enriched /
              {' '}{leads.length} total
            </Typography>
          </Paper>
        </Grid>

        {/* Score Distribution */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Score Distribution</Typography>
            <Typography sx={{ fontSize: '2rem', fontWeight: 700 }}>
              {leads.length > 0
                ? Math.round(leads.reduce((sum, p) => sum + (p.icpMatchPercentage || 0), 0) / leads.length)
                : 0}%
            </Typography>
            <Typography sx={{ color: '#718096' }}>Average ICP match</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Due Follow-ups */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1">Due Follow-ups ({allFollowUps.length})</Typography>
        </Stack>
        {allFollowUps.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No overdue follow-ups</Typography></Box>
        ) : (
          <AzotteGrid
            columns={followUpColumns}
            rows={allFollowUps}
            totalCount={allFollowUps.length}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            loading={false}
          />
        )}
      </Paper>
    </Box>
  );
}

// ─── SCORING TAB ───
function ScoringTab({ setError }) {
  const [leads, setLeads] = useState([]);
  const [icpProfiles, setIcpProfiles] = useState([]);
  const [selectedIcp, setSelectedIcp] = useState('');
  const [loading, setLoading] = useState(true);
  const [scoringId, setScoringId] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [bulkScoring, setBulkScoring] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      const [p, i] = await Promise.all([getLeads(), getIcpProfiles()]);
      setLeads(p.data);
      setIcpProfiles(i.data);
      if (i.data.length > 0) {
        const def = i.data.find(x => x.isDefault);
        setSelectedIcp(def ? def.id : i.data[0].id);
      }
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, [setError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleScore = async (leadId) => {
    setScoringId(leadId);
    setScoreResult(null);
    try {
      const { data } = await scoreLead(leadId, selectedIcp || undefined);
      setScoreResult(data);
      fetchData();
    } catch (err) { setError(err.response?.data?.message || 'Scoring failed'); }
    finally { setScoringId(null); }
  };

  const handleBulkScore = async () => {
    setBulkScoring(true);
    try {
      await bulkScoreLeads({ icpProfileId: selectedIcp || undefined });
      fetchData();
    } catch (err) { setError(err.response?.data?.message || 'Bulk scoring failed'); }
    finally { setBulkScoring(false); }
  };

  const fitColor = (f) => f === 'HIGH' ? '#38a169' : f === 'MEDIUM' ? '#dd6b20' : '#718096';

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  const scoringColumns = [
    { key: 'companyName', header: 'Company', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    { key: 'icpMatchPercentage', header: 'ICP Match', render: (val) => val != null ? `${val}%` : '-' },
    { key: 'enrichmentCompleteness', header: 'Enrichment', render: (val) => val != null ? `${val}%` : '-' },
    { key: 'azotteFitScore', header: 'Fit Score', render: (val) => val ?? '-' },
    {
      key: 'id', header: 'Actions', render: (_val, row) => (
        <Button variant="outlined" size="small" onClick={() => handleScore(row.id)} disabled={scoringId === row.id || !selectedIcp}>
          {scoringId === row.id ? '...' : 'Score'}
        </Button>
      )
    },
  ];

  return (
    <Box>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', mb: 2 }}>
        <TextField select size="small" sx={{ maxWidth: 250 }} value={selectedIcp} onChange={e => setSelectedIcp(e.target.value)}>
          <MenuItem value="">— Select ICP —</MenuItem>
          {icpProfiles.map(p => <MenuItem key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (Default)' : ''}</MenuItem>)}
        </TextField>
        <Button variant="contained" onClick={handleBulkScore} disabled={bulkScoring || !selectedIcp}>
          {bulkScoring ? 'Scoring...' : 'Bulk Score All'}
        </Button>
      </Stack>

      <Paper sx={{ p: 3, mb: 2.5 }}>
        <AzotteGrid
          columns={scoringColumns}
          rows={leads}
          totalCount={leads.length}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          loading={false}
        />
      </Paper>

      {/* Score Result Detail */}
      {scoreResult && (
        <Paper sx={{ p: 3, mb: 2.5, background: '#f7fafc' }}>
          <Stack direction="row" sx={{ gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 1 }}>
            <Chip label={scoreResult.fitLevel} sx={{ background: fitColor(scoreResult.fitLevel), color: '#fff', fontSize: '0.9rem', px: 1.5 }} />
            <Typography>ICP: {scoreResult.icpMatchPercentage}%</Typography>
          </Stack>
          {scoreResult.matchedCriteria?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#38a169' }}>Matched:</Typography>
              <Box component="ul" sx={{ m: '4px 0', pl: 2.5 }}>
                {scoreResult.matchedCriteria.map((m, i) => (
                  <li key={i} style={{ color: '#38a169' }}>&#10004; {m.label} ({m.field})</li>
                ))}
              </Box>
            </Box>
          )}
          {scoreResult.matchedSignals?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#3182ce' }}>Signals:</Typography>
              <Box component="ul" sx={{ m: '4px 0', pl: 2.5 }}>
                {scoreResult.matchedSignals.map((s, i) => (
                  <li key={i}>&#9889; {s.text} <Chip label={s.strength} size="small" sx={{ ml: 0.5 }} /> (w:{s.weight})</li>
                ))}
              </Box>
            </Box>
          )}
          {scoreResult.concerns?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#dd6b20' }}>Concerns:</Typography>
              <Box component="ul" sx={{ m: '4px 0', pl: 2.5 }}>
                {scoreResult.concerns.map((c, i) => (
                  <li key={i} style={{ color: '#dd6b20' }}>&#9888; {c.field}: {c.issue} — {c.impact}</li>
                ))}
              </Box>
            </Box>
          )}
          {scoreResult.disqualifiers?.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontWeight: 700, color: '#e53e3e' }}>Disqualifiers:</Typography>
              <Box component="ul" sx={{ m: '4px 0', pl: 2.5 }}>
                {scoreResult.disqualifiers.map((d, i) => (
                  <li key={i} style={{ color: '#e53e3e' }}>&#10060; {d.rule}: {d.matched}</li>
                ))}
              </Box>
            </Box>
          )}
          <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => setScoreResult(null)}>Close</Button>
        </Paper>
      )}
    </Box>
  );
}

// ─── ICP PROFILES TAB ───
function IcpProfilesTab({ setError }) {
  const dialog = useDialog();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null, 'add', or profile object
  const [form, setForm] = useState({});
  const [template, setTemplate] = useState(null);
  const [signals, setSignals] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        getIcpProfiles(),
        getActiveSignals(),
      ]);
      setProfiles(p.data);
      setSignals(s.data);
      getDefaultProfilingTemplate().then(r => setTemplate(r.data)).catch(() => {});
    } catch { setError('Failed to load ICP profiles'); }
    finally { setLoading(false); }
  }, [setError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => {
    setForm({ name: '', description: '', targetCriteria: '{}', signalWeights: '', minScore: 0, isDefault: false });
    setModal('add');
  };

  const openEdit = async (profile) => {
    try {
      const { data } = await getIcpProfile(profile.id);
      setForm({
        name: data.name, description: data.description || '',
        targetCriteria: data.targetCriteria, signalWeights: data.signalWeights || '',
        minScore: data.minScore, isDefault: data.isDefault,
      });
      setModal(data);
    } catch { setError('Failed to load profile'); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...form,
        targetCriteria: form.targetCriteria || '{}',
        signalWeights: form.signalWeights || null,
      };
      if (modal === 'add') await createIcpProfile(payload);
      else await updateIcpProfile(modal.id, payload);
      setModal(null);
      fetchData();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete ICP profile', message: 'Delete this ICP profile?', confirmText: 'Delete', danger: true }))) return;
    try { await deleteIcpProfile(id); fetchData(); }
    catch { setError('Failed to delete'); }
  };

  // Parse template to get field options for criteria builder
  let templateFields = [];
  if (template?.schema) {
    try {
      const schema = JSON.parse(template.schema);
      (schema.sections || []).forEach(section => {
        (section.fields || []).forEach(field => {
          templateFields.push({ key: field.key, label: field.label, options: field.options || [] });
        });
      });
    } catch {}
  }

  // Parse current criteria for the builder
  let criteriaObj = {};
  try { criteriaObj = JSON.parse(form.targetCriteria || '{}'); } catch {}

  const toggleCriteriaValue = (fieldKey, value) => {
    const current = criteriaObj[fieldKey] || [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    const newCriteria = { ...criteriaObj };
    if (updated.length > 0) newCriteria[fieldKey] = updated;
    else delete newCriteria[fieldKey];
    setForm({ ...form, targetCriteria: JSON.stringify(newCriteria) });
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  const profileColumns = [
    { key: 'name', header: 'Name', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    { key: 'isDefault', header: 'Default', render: (val) => val ? <Chip label="Default" size="small" color="primary" /> : '-' },
    { key: 'criteriaFieldCount', header: 'Criteria Fields' },
    { key: 'minScore', header: 'Min Score' },
    {
      key: 'id', header: 'Actions', render: (_val, row) => (
        <Stack direction="row" sx={{ gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => openEdit(row)}>Edit</Button>
          <Button variant="outlined" size="small" color="error" onClick={() => handleDelete(row.id)}>Delete</Button>
        </Stack>
      )
    },
  ];

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle1">ICP Profiles</Typography>
          <Button variant="contained" size="small" onClick={openAdd}>+ Add ICP Profile</Button>
        </Stack>
        {profiles.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 1 }}>No ICP profiles yet</Typography>
            <Button variant="contained" onClick={openAdd}>Create First Profile</Button>
          </Box>
        ) : (
          <AzotteGrid
            columns={profileColumns}
            rows={profiles}
            totalCount={profiles.length}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
            loading={false}
          />
        )}
      </Paper>

      {modal && (
        <Modal title={modal === 'add' ? 'Add ICP Profile' : 'Edit ICP Profile'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave}>
            <Box sx={{ mb: 2 }}>
              <TextField label="Name *" size="small" fullWidth required value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField label="Description" size="small" fullWidth multiline rows={2} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
            </Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField label="Min Score" size="small" fullWidth type="number" slotProps={{ htmlInput: { min: 0, max: 100 } }} value={form.minScore || 0} onChange={e => setForm({ ...form, minScore: parseInt(e.target.value) || 0 })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={<Checkbox checked={form.isDefault || false} onChange={e => setForm({ ...form, isDefault: e.target.checked })} />}
                  label="Default Profile"
                />
              </Grid>
            </Grid>

            {/* Target Criteria Builder */}
            {templateFields.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Target Criteria (select matching values per field)</Typography>
                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5 }}>
                  {templateFields.filter(f => f.options?.length > 0).map(field => (
                    <Box key={field.key} sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: 0.5 }}>{field.label}</Typography>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {field.options.map(opt => {
                          const selected = (criteriaObj[field.key] || []).includes(opt);
                          return (
                            <Chip key={opt} label={opt} size="small" variant={selected ? 'filled' : 'outlined'}
                              color={selected ? 'primary' : 'default'}
                              onClick={() => toggleCriteriaValue(field.key, opt)}
                              sx={{ cursor: 'pointer' }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Signal Weights */}
            {signals.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Signal Weight Overrides (optional)</Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5 }}>
                  {signals.map(sig => {
                    let weights = {};
                    try { weights = JSON.parse(form.signalWeights || '{}'); } catch {}
                    return (
                      <Stack key={sig.id} direction="row" sx={{ alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ flex: 1, fontSize: '0.85rem' }}>{sig.text} <Chip label={sig.category} size="small" sx={{ ml: 0.5 }} /></Typography>
                        <TextField size="small" type="number" slotProps={{ htmlInput: { min: 0, max: 100 } }}
                          sx={{ width: 80 }}
                          placeholder={String(sig.weight)}
                          value={weights[sig.id] ?? ''}
                          onChange={e => {
                            const w = { ...weights };
                            if (e.target.value) w[sig.id] = parseInt(e.target.value);
                            else delete w[sig.id];
                            setForm({ ...form, signalWeights: Object.keys(w).length > 0 ? JSON.stringify(w) : '' });
                          }} />
                      </Stack>
                    );
                  })}
                </Box>
              </Box>
            )}

            <DialogActions sx={{ px: 0 }}>
              <Button variant="outlined" onClick={() => setModal(null)}>Cancel</Button>
              <Button variant="contained" type="submit">{modal === 'add' ? 'Create Profile' : 'Save Changes'}</Button>
            </DialogActions>
          </form>
        </Modal>
      )}
    </Box>
  );
}

// ─── ENRICHMENT TAB ───
function EnrichmentTab({ setError }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrichingId, setEnrichingId] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      const [p, prov] = await Promise.all([getLeads(), getAiProviders()]);
      setLeads(p.data);
      const provList = prov.data || [];
      setProviders(provList);
      const available = provList.filter(x => x.isAvailable);
      const openai = available.find(x => (x.name || '').toLowerCase() === 'openai');
      if (openai) setSelectedProvider('openai');
      else if (available.length > 0) setSelectedProvider(available[0].name);
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, [setError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEnrich = async (leadId) => {
    setEnrichingId(leadId);
    setReviewData(null);
    try {
      const { data } = await aiEnrichLead(leadId, selectedProvider || undefined);
      setReviewData(data);
    } catch (err) { setError(err.response?.data?.message || 'Enrichment failed'); }
    finally { setEnrichingId(null); }
  };

  const handleReview = async (decisions) => {
    try {
      await submitEnrichmentReview({ leadId: reviewData.leadId, decisions });
      setReviewData(null);
      fetchData();
    } catch (err) { setError(err.response?.data?.message || 'Review failed'); }
  };

  const confColor = (c) => c >= 0.8 ? '#38a169' : c >= 0.5 ? '#dd6b20' : '#e53e3e';

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  const enrichColumns = [
    { key: 'companyName', header: 'Company', render: (val) => <Typography sx={{ fontWeight: 500 }}>{val}</Typography> },
    {
      key: 'enrichmentCompleteness', header: 'Completeness', render: (val) => (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <LinearProgress variant="determinate" value={val || 0} sx={{ flex: 1, maxWidth: 120, height: 8, borderRadius: 1 }} />
          <Typography sx={{ fontSize: '0.85rem' }}>{val ?? 0}%</Typography>
        </Stack>
      )
    },
    {
      key: 'id', header: 'Actions', render: (_val, row) => (
        <Button variant="contained" size="small" onClick={() => handleEnrich(row.id)} disabled={enrichingId === row.id}>
          {enrichingId === row.id ? 'Enriching...' : 'AI Enrich'}
        </Button>
      )
    },
  ];

  return (
    <Box>
      <Stack direction="row" sx={{ gap: 1.5, alignItems: 'center', mb: 2 }}>
        <TextField select size="small" label="AI Engine" sx={{ maxWidth: 250, minWidth: 200 }} value={selectedProvider} onChange={e => setSelectedProvider(e.target.value)}>
          <MenuItem value="">System Default</MenuItem>
          {providers.map(p => <MenuItem key={p.name} value={p.name}>{p.displayName}</MenuItem>)}
        </TextField>
      </Stack>

      <Paper sx={{ p: 3, mb: 2.5 }}>
        <AzotteGrid
          columns={enrichColumns}
          rows={leads}
          totalCount={leads.length}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          loading={false}
        />
      </Paper>

      {/* Enrichment Review Modal */}
      {reviewData && (
        <Modal title="Review AI Enrichment Suggestions" onClose={() => setReviewData(null)}>
          <Typography sx={{ mb: 1.5, fontSize: '0.85rem', color: '#718096' }}>
            Completeness: {reviewData.completenessBefore}% → review to improve
          </Typography>
          {(!reviewData.suggestions || reviewData.suggestions.length === 0) ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No suggestions returned. The lead may already be fully enriched, or the AI provider returned no data.</Typography>
            </Box>
          ) : (
            <Box>
              <Stack direction="row" sx={{ gap: 1, mb: 1.5 }}>
                <Button variant="contained" size="small"
                  onClick={() => handleReview(reviewData.suggestions.map(s => ({ fieldName: s.fieldName, action: 'Accept' })))}>
                  Accept All
                </Button>
                <Button variant="outlined" size="small"
                  onClick={() => handleReview(reviewData.suggestions.map(s => ({ fieldName: s.fieldName, action: 'Reject' })))}>
                  Reject All
                </Button>
              </Stack>
              {reviewData.suggestions.map((s, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1, background: '#f7fafc' }}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{s.fieldName}</Typography>
                      <Typography sx={{ fontSize: '0.85rem', mt: 0.5 }}>
                        <Box component="span" sx={{ color: '#718096' }}>Current: </Box>
                        {s.currentValue || <em style={{ color: '#a0aec0' }}>empty</em>}
                      </Typography>
                      <Typography sx={{ fontSize: '0.85rem' }}>
                        <Box component="span" sx={{ color: '#718096' }}>Suggested: </Box>
                        <strong>{s.suggestedValue}</strong>
                      </Typography>
                      <Stack direction="row" sx={{ mt: 0.5, gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography sx={{ color: confColor(s.confidence), fontWeight: 600, fontSize: '0.8rem' }}>
                          {Math.round(s.confidence * 100)}% confident
                        </Typography>
                        {s.source && <Chip label={s.source} size="small" />}
                      </Stack>
                      {s.evidence && <Typography sx={{ fontSize: '0.8rem', color: '#4a5568', mt: 0.25 }}>{s.evidence}</Typography>}
                    </Box>
                    <Stack direction="row" sx={{ gap: 0.5 }}>
                      <Button variant="contained" size="small"
                        onClick={() => handleReview([{ fieldName: s.fieldName, action: 'Accept', editedValue: s.suggestedValue }])}>
                        Accept
                      </Button>
                      <Button variant="outlined" size="small" color="error"
                        onClick={() => handleReview([{ fieldName: s.fieldName, action: 'Reject' }])}>
                        Reject
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ))}
            </Box>
          )}
        </Modal>
      )}
    </Box>
  );
}
